/**
 * The one place the waitlist provider is called.
 *
 * The backend is a Cloudflare Pages Function (functions/api/waitlist.ts)
 * backed by D1. Everything the site knows about signing up lives behind
 * this function, so swapping providers means editing this file and nothing
 * else. Do not call a provider SDK from a component.
 *
 * Configure with VITE_SUBSCRIBE_ENDPOINT (see .env.example) — set to
 * "/api/waitlist" for both production and preview. When it is unset, e.g.
 * local `npm run dev`, the call resolves optimistically so the success
 * state is still reachable without a backend.
 */

import { EMAIL_RE } from './email'

export type SubscribeResult = { ok: true } | { ok: false; error: string }

/** Shown for any non-2xx or network failure. The mock does not design this. */
const GENERIC_ERROR = 'Something broke on our end — try again in a moment.'

const TIMEOUT_MS = 10_000

export async function subscribe(email: string): Promise<SubscribeResult> {
  const endpoint = import.meta.env.VITE_SUBSCRIBE_ENDPOINT

  if (!endpoint) {
    if (import.meta.env.DEV) {
      console.warn('[subscribe] VITE_SUBSCRIBE_ENDPOINT is unset — resolving optimistically.', {
        email,
      })
    }
    return { ok: true }
  }

  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS)

  try {
    const params = new URLSearchParams(window.location.search)

    const res = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: email.trim(),
        source: 'landing',
        utm_source: params.get('utm_source') ?? undefined,
        utm_medium: params.get('utm_medium') ?? undefined,
        utm_campaign: params.get('utm_campaign') ?? undefined,
      }),
      signal: controller.signal,
    })

    if (!res.ok) {
      return { ok: false, error: GENERIC_ERROR }
    }

    return { ok: true }
  } catch {
    // Network failure, CORS, or the timeout above.
    return { ok: false, error: GENERIC_ERROR }
  } finally {
    clearTimeout(timer)
  }
}

/** Client-side check, run on submit only — never while typing. */
export function isValidEmail(value: string): boolean {
  return EMAIL_RE.test(value.trim())
}
